import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/utils/invokeEdgeFunction";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import {
  Mail,
  Link as LinkIcon,
  Copy,
  Users,
  FileText,
  Send,
  RefreshCw,
  UserPlus,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
} from "lucide-react";
import { CANDIDATE_STATUS, EVALUATION_ROUNDS } from "@/types/enums";
import type { Tables } from "@/integrations/supabase/types";

type College = Tables<"colleges">;

const ASSESSMENT_ROUNDS = new Set<string>([
  EVALUATION_ROUNDS.MCQ,
  EVALUATION_ROUNDS.OPEN_ENDED,
]);

interface CollegeAssessmentActionsProps {
  college: College;
}

export function CollegeAssessmentActions({
  college,
}: CollegeAssessmentActionsProps) {
  const [isOpen, setIsOpen] = useState(false);

  // --- CV Profile Evaluation State ---
  const [candidatesToEvaluate, setCandidatesToEvaluate] = useState<any[]>([]);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<
    { id: string; status: "success" | "error"; error?: string }[]
  >([]);

  // Fetch candidates without cv_profile score when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const fetchCandidates = async () => {
      setCandidatesToEvaluate([]);
      setResults([]);
      setProgress(0);
      setIsEvaluating(false);
      try {
        const { data: candidates, error } = await (supabase as any).rpc(
          "candidates_missing_cv_profile",
          { college_id: college.id }
        );
        if (error) throw error;
        setCandidatesToEvaluate(candidates || []);
      } catch (err: any) {
        toast.error("Error fetching candidates", {
          description: err.message,
        });
      }
    };
    fetchCandidates();
  }, [isOpen, college.id]);

  // Evaluation logic
  const handleEvaluateCVs = async () => {
    setIsEvaluating(true);
    setResults([]);
    let completed = 0;
    const total = candidatesToEvaluate.length;

    if (total === 0) {
      toast.info("No candidates to evaluate");
      setIsEvaluating(false);
      return;
    }

    const newResults: {
      id: string;
      status: "success" | "error";
      error?: string;
    }[] = [];
    for (const candidate of candidatesToEvaluate) {
      try {
        const { error } = await supabase.functions.invoke(
          "evaluate-cv-gemini",
          {
            body: {
              candidateId: candidate.id,
              positionApplied: candidate.position_applied,
            },
          }
        );
        if (error) {
          newResults.push({
            id: candidate.id,
            status: "error",
            error: error.message,
          });
        } else {
          newResults.push({ id: candidate.id, status: "success" });
        }
      } catch (err: any) {
        newResults.push({
          id: candidate.id,
          status: "error",
          error: err.message,
        });
      }
      completed++;
      setProgress(Math.round((completed / total) * 100));
      setResults([...newResults]);
    }
    setIsEvaluating(false);
    const errors = newResults.filter((r) => r.status === "error").length;
    if (errors > 0) {
      toast.error("Evaluation completed with errors", {
        description: `${total - errors} succeeded, ${errors} failed`,
      });
    } else {
      toast.success("Evaluation complete", {
        description: `Evaluated ${total} candidates.`,
      });
    }
  };

  // Get candidate statistics for this college
  const { data: candidateStats } = useQuery({
    queryKey: ["college-candidate-stats", college.id],
    queryFn: async () => {
      const currentYear = new Date().getFullYear();
      const yearStart = `${currentYear}-01-01T00:00:00.000Z`;
      const yearEnd = `${currentYear + 1}-01-01T00:00:00.000Z`;

      const { data, error } = await supabase
        .from("candidates")
        .select("status, current_round")
        .eq("college_id", college.id)
        .gte("created_at", yearStart)
        .lt("created_at", yearEnd);

      if (error) throw error;

      const stats = {
        total: data.length,
        cvProfilePassed: data.filter(
          (c) =>
            c.current_round === EVALUATION_ROUNDS.CV_PROFILE &&
            c.status == CANDIDATE_STATUS.PASSED
        ).length,
        assessmentSent: data.filter(
          (c) => c.status === CANDIDATE_STATUS.ASSESSMENT_SENT
        ).length,
        inProgress: data.filter(
          (c) => c.status === CANDIDATE_STATUS.ASSESSMENT_IN_PROGRESS
        ).length,
        passedAssessment: data.filter(
          (c) =>
            c.status === CANDIDATE_STATUS.PASSED &&
            ASSESSMENT_ROUNDS.has(c.current_round ?? "")
        ).length,
      };

      return stats;
    },
    enabled: isOpen,
    refetchInterval: isOpen ? 10000 : false,
  });

  const sendBulkEmailMutation = useMutation({
    mutationFn: async () => {
      await invokeEdgeFunction(
        "send-bulk-assessment",
        {
          body: {
            collegeId: college.id,
          },
        }
      );
    },
    onSuccess: () => {
      toast.success("Bulk assessment emails sent successfully!", {
        description: `Assessment links have been sent to all eligible candidates from ${college.name}`,
      });

      setIsOpen(false);
    },
    onError: (error) => {
      console.error("Error sending bulk emails:", error);
      toast.error("Failed to send bulk assessment emails", {
        description:
          "Please try again or contact support if the issue persists.",
      });
    },
  });

  const resendBulkEmailMutation = useMutation({
    mutationFn: async () => {
      await invokeEdgeFunction(
        "resend-bulk-assessment",
        {
          body: {
            collegeId: college.id,
          },
        }
      );
    },
    onSuccess: () => {
      toast.success("Assessment emails resent successfully!", {
        description: `Updated assessment links have been sent to candidates from ${college.name}`,
      });

      setIsOpen(false);
    },
    onError: (error) => {
      console.error("Error resending bulk emails:", error);
      toast.error("Failed to resend assessment emails", {
        description:
          "Please try again or contact support if the issue persists.",
      });
    },
  });

  const registrationUrl = `${window.location.origin}/register/${college.college_code}`;

  const copyRegistrationLink = async () => {
    try {
      await navigator.clipboard.writeText(registrationUrl);
      toast.success("Registration link copied!", {
        description: "Share this link with students for registration",
      });
    } catch (err: any) {
      toast.error("Failed to copy registration link", {
        description: err.message,
      });
    }
  };

  const canSendBulkEmail = candidateStats && candidateStats.cvProfilePassed > 0;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Assessment management">
          <FileText className="h-4 w-4" />
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Assessment Management - {college.name}
          </DialogTitle>
          <DialogDescription>
            Manage candidate registration and send assessment links for this
            college
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Candidate Statistics */}
          {candidateStats && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Candidate Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {candidateStats.total}
                    </div>
                    <div className="text-sm text-blue-600">Total</div>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">
                      {candidateStats.cvProfilePassed}
                    </div>
                    <div className="text-sm text-yellow-600">
                      CV Profile Passed
                    </div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">
                      {candidateStats.assessmentSent}
                    </div>
                    <div className="text-sm text-orange-600">
                      Assessment Sent
                    </div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {candidateStats.inProgress}
                    </div>
                    <div className="text-sm text-purple-600">
                      Assessment In Progress
                    </div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {candidateStats.passedAssessment}
                    </div>
                    <div className="text-sm text-green-600">Passed</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* CV Profile Evaluation Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="h-4 w-4" />
                CV Profile Evaluation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-2">
                <div className="text-base font-medium text-gray-700">
                  {candidatesToEvaluate.length === 0
                    ? "All candidates already have a CV profile score."
                    : `${candidatesToEvaluate.length} candidate${
                        candidatesToEvaluate.length > 1 ? "s" : ""
                      } do not have a CV profile score.`}
                </div>
                {candidatesToEvaluate.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={handleEvaluateCVs}
                      disabled={isEvaluating}
                      aria-label="Evaluate CVs"
                      className="w-fit"
                    >
                      {isEvaluating ? "Evaluating..." : "Evaluate"}
                    </Button>
                    {isEvaluating && (
                      <div className="w-full">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="bg-blue-500 h-2 transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {progress}% complete
                        </div>
                      </div>
                    )}
                    {results.length > 0 && (
                      <div className="text-sm mt-2">
                        <div className="text-green-700">
                          Success:{" "}
                          {results.filter((r) => r.status === "success").length}
                        </div>
                        {results.filter((r) => r.status === "error").length >
                          0 && (
                          <div className="text-red-700">
                            Failed:{" "}
                            {results.filter((r) => r.status === "error").length}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Registration Link Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Student Registration
              </CardTitle>
              <CardDescription>
                Share this link with students to register for the assessment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Label htmlFor="registration-url">Registration URL</Label>
                <div className="flex space-x-2">
                  <Input
                    id="registration-url"
                    value={registrationUrl}
                    readOnly
                    className="flex-1 font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={copyRegistrationLink}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Students can use this link to register and submit their CV for
                  the assessment
                </p>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Bulk Assessment Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Send className="h-4 w-4" />
                Bulk Assessment Distribution
              </CardTitle>
              <CardDescription>
                Send assessment links to all eligible candidates
              </CardDescription>
            </CardHeader>
          <CardContent>
            <div className="space-y-4">
                {!canSendBulkEmail ? (
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="font-medium text-gray-700">
                        No eligible candidates
                      </p>
                      <p className="text-sm text-gray-600">
                        There are no candidates with CV profile passed to send
                        assessment links to or assessment has already been sent
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-700">
                          {candidateStats?.cvProfilePassed} candidates ready
                        </p>
                        <p className="text-sm text-green-600">
                          Assessment links will be sent to candidates with "CV
                          Submitted" status
                        </p>
                      </div>
                    </div>

                    <Button
                      onClick={() => sendBulkEmailMutation.mutate()}
                      disabled={sendBulkEmailMutation.isPending}
                      className="w-full"
                      size="lg"
                    >
                      {sendBulkEmailMutation.isPending ? (
                        <>
                          <Clock className="h-4 w-4 mr-2 animate-spin" />
                          Sending Assessment Links...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4 mr-2" />
                          Send Assessment Links to{" "}
                          {candidateStats?.cvProfilePassed} Candidates
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {candidateStats?.assessmentSent > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg">
                      <RefreshCw className="h-5 w-5 text-yellow-600" />
                      <div>
                        <p className="font-medium text-yellow-700">
                          {candidateStats.assessmentSent} candidates previously notified
                        </p>
                        <p className="text-sm text-yellow-600">
                          Resend assessment emails with updated schedule
                        </p>
                      </div>
                    </div>

                    <Button
                      onClick={() => resendBulkEmailMutation.mutate()}
                      disabled={resendBulkEmailMutation.isPending}
                      className="w-full"
                      size="lg"
                      variant="outline"
                    >
                      {resendBulkEmailMutation.isPending ? (
                        <>
                          <Clock className="h-4 w-4 mr-2 animate-spin" />
                          Resending Assessment Emails...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Resend Assessment Emails to {candidateStats.assessmentSent} Candidates
                        </>
                      )}
                    </Button>
                  </div>
                )}

                <div className="text-sm text-muted-foreground bg-blue-50 p-3 rounded-lg">
                  <p className="font-medium text-blue-800 mb-1">
                    What happens when you send?
                  </p>
                  <ul className="text-blue-700 space-y-1">
                    <li>
                      • Unique assessment tokens are generated for each
                      candidate
                    </li>
                    <li>
                      • Email notifications are sent with assessment
                      instructions
                    </li>
                    <li>• Candidate status is updated to "Assessment Sent"</li>
                    <li>• Links expire after 7 days for security</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </DialogContent>
    </Dialog>
  );
}
